import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const { i18n, t } = useTranslation()

  const navLinks = [
    { path: '/', label: t('nav.home', 'Home') },
    { path: '/about', label: t('nav.about', 'About') },
    { path: '/services', label: t('nav.services', 'Services') },
    { path: '/contact', label: t('nav.contact', 'Contact') }
  ]

  const isActivePath = (path: string) => {
    return location.pathname === path
  }

  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
  }

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en'
    i18n.changeLanguage(newLang)
  }

  return (
    <>
      <nav className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="relative flex items-center justify-between h-16 md:h-20">
            {/* Logo - with text on desktop */}
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/da/unnamed%20(12)%20(1).jpg"
                alt="Digital Amigos Logo"
                className="h-10 md:h-12 w-auto rounded-lg"
                crossOrigin="anonymous"
              />
              <span className="hidden md:block text-xl md:text-2xl font-bold text-gray-800">
                Digital <span className="text-baby-blue-600">Amigos</span>
              </span>
            </Link>

            {/* Digital Amigos Text - centered on mobile only */}
            <span className="absolute left-1/2 -translate-x-1/2 md:hidden text-lg font-bold text-gray-800">
              Digital <span className="text-baby-blue-600">Amigos</span>
            </span>

            {/* Desktop Navigation - hidden on mobile */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-base lg:text-lg font-medium transition-colors duration-200 hover:text-baby-blue-600 ${
                    isActivePath(link.path)
                      ? 'text-baby-blue-600 border-b-2 border-baby-blue-600 pb-1'
                      : 'text-gray-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Language Toggle Button */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-baby-blue-100 hover:bg-baby-blue-200 text-baby-blue-700 font-medium transition-colors duration-200"
                aria-label="Toggle language"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span className="text-sm font-semibold">{i18n.language === 'en' ? 'ES' : 'EN'}</span>
              </button>
            </div>

            {/* Mobile Actions - visible on mobile, hidden on desktop */}
            <div className="md:hidden flex items-center gap-2">
              {/* Language Toggle Button - Mobile */}
              <button
                onClick={toggleLanguage}
                className="p-2 rounded-lg bg-baby-blue-100 hover:bg-baby-blue-200 text-baby-blue-700 transition-colors"
                aria-label="Toggle language"
              >
                <span className="text-sm font-semibold">{i18n.language === 'en' ? 'ES' : 'EN'}</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Toggle mobile menu"
                aria-expanded={mobileMenuOpen}
              >
                <svg
                  className="w-6 h-6 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          {/* Slide-out Menu */}
          <div className="fixed right-0 top-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="p-6">
              {/* Close Button */}
              <button
                onClick={closeMobileMenu}
                className="mb-8 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close mobile menu"
              >
                <svg
                  className="w-6 h-6 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Mobile Navigation Links */}
              <nav className="flex flex-col space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={closeMobileMenu}
                    className={`min-h-12 px-4 py-3 rounded-lg text-lg font-medium transition-colors duration-200 ${
                      isActivePath(link.path)
                        ? 'bg-baby-blue-100 text-baby-blue-700 border-l-4 border-baby-blue-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Navigation
